<div [class.read-only-mode]="isReadOnly()">
  <!-- Toast для сообщений -->
  <p-toast role="status" aria-live="polite" aria-atomic="true" />

  <!-- Live Region for Screen Readers -->
  <div role="status" aria-live="polite" aria-atomic="true" class="visually-hidden">
    {{ liveRegionMessage() }}
  </div>

  <!-- Page Header -->
  <section class="mb-4">
    <h1 class="text-3xl font-bold text-900 mb-2">Dati Specifici: Legale</h1>
    <p class="text-600 m-0">Completa i dati professionali specifici per il Legale</p>
  </section>

  <!-- Instructions for accessibility -->
  @if (!isReadOnly()) {
    <div class="surface-100 border-round p-3 mb-4" role="region" aria-labelledby="form-instructions-title">
      <p id="form-instructions-title" class="text-sm text-700 m-0">
        <i class="pi pi-info-circle mr-2" aria-hidden="true"></i>
        <strong>Istruzioni:</strong> Compila tutti i campi obbligatori contrassegnati con asterisco (*). Nella sezione "Sede Operativa" indica se la sede legale è anche operativa; in caso contrario aggiungi almeno una sede operativa. Puoi aggiungere più Aree di Competenza.
      </p>
    </div>
  }

  <!-- SECTION 1: INFORMAZIONI SPECIFICHE -->
  <section class="surface-0 border-round p-5 mb-4 shadow-1" role="region" aria-labelledby="section-info">
    <div class="flex align-items-center gap-3 mb-4 pb-3 border-bottom-1 border-200">
      <i class="pi pi-info-circle text-3xl text-primary" aria-hidden="true"></i>
      <div>
        <h2 id="section-info" class="text-2xl font-semibold m-0 text-900">Informazioni Specifiche</h2>
        <p class="text-600 m-0 mt-1">Dati professionali e iscrizioni</p>
      </div>
    </div>

    <div class="formgrid grid">
      <!-- Activity Start Date -->
      <div class="field col-12 md:col-4">
        <label for="activity-start-date" class="font-semibold block mb-2">
          Inizio Attività Professionale dal
          <span class="text-red-500" aria-hidden="true">*</span>
          <span class="visually-hidden">obbligatorio</span>
        </label>
        <p-datepicker
          id="activity-start-date"
          [ngModel]="activityStartDate()"
          (ngModelChange)="activityStartDate.set($event); markFieldAsTouched('activityStartDate')"
          dateFormat="dd/mm/yy"
          [maxDate]="today"
          [showIcon]="true"
          [showButtonBar]="true"
          placeholder="Seleziona data (gg/mm/aaaa)"
          inputId="activity-start-date-input"
          [attr.aria-label]="'Data inizio attività professionale'"
          [attr.aria-required]="true"
          [attr.aria-invalid]="(formSubmitted() || touchedFields.has('activityStartDate')) && !activityStartDate()"
          styleClass="w-full"
          (onBlur)="markFieldAsTouched('activityStartDate')"
          [disabled]="isReadOnly()"
        />
        @if ((formSubmitted() || touchedFields.has('activityStartDate')) && !activityStartDate()) {
          <small class="text-red-500" role="alert">Campo obbligatorio</small>
        }
      </div>

      <!-- Lawyer Order Registration -->
      <div class="field col-12 md:col-4">
        <label for="lawyer-order" class="font-semibold block mb-2">
          Iscrizione Ordine Avvocati di
          <span class="text-red-500" aria-hidden="true">*</span>
          <span class="visually-hidden">obbligatorio</span>
        </label>
        <input
          id="lawyer-order"
          type="text"
          pInputText
          [ngModel]="professionalOrder()"
          (ngModelChange)="professionalOrder.set($event); markFieldAsTouched('professionalOrder')"
          placeholder="Es: Milano"
          class="w-full"
          [disabled]="isReadOnly()"
          (blur)="markFieldAsTouched('professionalOrder')"
          [attr.aria-required]="true"
          [attr.aria-invalid]="(formSubmitted() || touchedFields.has('professionalOrder')) && !professionalOrder()"
        />
        @if ((formSubmitted() || touchedFields.has('professionalOrder')) && !professionalOrder()) {
          <small class="text-red-500" role="alert">Campo obbligatorio</small>
        }
      </div>

      <!-- Registration Number -->
      <div class="field col-12 md:col-4">
        <label for="registration-number" class="font-semibold block mb-2">
          N° Iscrizione
          <span class="text-red-500" aria-hidden="true">*</span>
          <span class="visually-hidden">obbligatorio</span>
        </label>
        <p-inputnumber
          inputId="registration-number"
          [ngModel]="orderRegistrationNumber()"
          (ngModelChange)="orderRegistrationNumber.set($event); markFieldAsTouched('orderRegistrationNumber')"
          [useGrouping]="false"
          placeholder="Inserisci numero iscrizione"
          styleClass="w-full"
          (onBlur)="markFieldAsTouched('orderRegistrationNumber')"
          [attr.aria-required]="true"
          [attr.aria-invalid]="(formSubmitted() || touchedFields.has('orderRegistrationNumber')) && !orderRegistrationNumber()"
        />
        @if ((formSubmitted() || touchedFields.has('orderRegistrationNumber')) && !orderRegistrationNumber()) {
          <small class="text-red-500" role="alert">Campo obbligatorio</small>
        }
      </div>

      <!-- Assigned Companies -->
      <div class="field col-12">
        <label for="assigned-companies" class="font-semibold block mb-2">
          Compagnie Assicurative per le quali svolge collaborazioni
        </label>
        <textarea
          id="assigned-companies"
          pInputTextarea
          rows="3"
          [ngModel]="assignedCompanies()"
          (ngModelChange)="onAssignedCompaniesChange($event)"
          placeholder="Es: AXA Italia, AXA REAS, altre compagnie..."
          class="w-full"
          (blur)="markFieldAsTouched('assignedCompanies')"
        ></textarea>
      </div>

      <!-- Operating Territories -->
      <div class="field col-12">
        <label for="operating-territories" class="font-semibold block mb-2">
          Territorio operativo
          <span class="text-red-500" aria-hidden="true">*</span>
          <span class="visually-hidden">obbligatorio</span>
        </label>
        @if (isReadOnly()) {
          <!-- In sola lettura mostriamo i territori come chip di testo: il
               p-multiselect disabilitato non rende i chip selezionati. -->
          <div id="operating-territories" class="flex flex-wrap gap-2">
            @for (label of operatingTerritoriesLabels(); track label) {
              <span class="inline-flex align-items-center surface-200 text-900 border-round px-2 py-1 text-sm">{{ label }}</span>
            } @empty {
              <span class="text-600">—</span>
            }
          </div>
        } @else {
          <p-multiselect
            id="operating-territories"
            [ngModel]="operatingTerritories()"
            (ngModelChange)="operatingTerritories.set($event)"
            [options]="allProvincesOptions()"
            optionLabel="label"
            optionValue="value"
            placeholder="Seleziona Provincia"
            [filter]="true"
            [showToggleAll]="true"
            [showClear]="true"
            display="chip"
            [maxSelectedLabels]="allProvincesOptions().length"
            styleClass="w-full"
            (blur)="markFieldAsTouched('operatingTerritories')"
            [attr.aria-required]="true"
            [attr.aria-invalid]="(formSubmitted() || touchedFields.has('operatingTerritories')) && (!operatingTerritories() || operatingTerritories().length === 0)"
            [disabled]="isReadOnly()"
          />
          @if ((formSubmitted() || touchedFields.has('operatingTerritories')) && (!operatingTerritories() || operatingTerritories().length === 0)) {
            <small class="text-red-500" role="alert">Campo obbligatorio</small>
          }
        }
      </div>
    </div>
  </section>

  <!-- SECTION 2: FORMA DI ATTIVITÀ -->
  <section class="surface-0 border-round p-5 mb-4 shadow-1" role="region" aria-labelledby="section-activity-form">
    <div class="flex align-items-center gap-3 mb-4 pb-3 border-bottom-1 border-200">
      <i class="pi pi-briefcase text-3xl text-primary" aria-hidden="true"></i>
      <div>
        <h2 id="section-activity-form" class="text-2xl font-semibold m-0 text-900">Forma di Attività</h2>
        @if (!isReadOnly()) {
          <p class="text-600 m-0 mt-1">Forma di svolgimento dell'attività e denominazione dello studio</p>
        }
      </div>
    </div>

    <div class="formgrid grid">
      <!--Activity Form -->
      <div class="field col-12 md:col-4">
        <label for="activity-form" class="font-semibold block mb-2">
          Il fiduciario svolge la propria attività in forma
        </label>
        <p-select
          id="activity-form"
          [ngModel]="activityForm()"
          (ngModelChange)="activityForm.set($event); markFieldAsTouched('activityForm')"
          [options]="activityFormOptions()"
          placeholder="-- Seleziona --"
          optionLabel="label"
          optionValue="value"
          [showClear]="true"
          styleClass="w-full"
          [disabled]="isReadOnly()"
        />
      </div>

      <!-- Collective Form Type (conditional) -->
      @if (isCollectiveForm()) {
        <div class="field col-12 md:col-4">
          <label for="collective-form-type" class="font-semibold block mb-2">
            Se in forma collettiva, indicare
          </label>
          <p-select
            id="collective-form-type"
            [ngModel]="collectiveType()"
            (ngModelChange)="collectiveType.set($event); markFieldAsTouched('collectiveType')"
            [options]="collectiveTypeOptions()"
            placeholder="-- Seleziona --"
            optionLabel="label"
            optionValue="value"
            [showClear]="true"
            styleClass="w-full"
            [disabled]="isReadOnly()"
          />
        </div>
      }

      <!-- Office Name -->
      <div class="field col-12">
        <label for="office-name" class="font-semibold block mb-2">
          Denominazione Studio
        </label>
        <input
          id="office-name"
          type="text"
          pInputText
          [ngModel]="firmName()"
          (ngModelChange)="firmName.set($event); markFieldAsTouched('firmName')"
          placeholder="Es: Studio Legale Rossi & Partners"
          class="w-full"
          [disabled]="isReadOnly()"
          (blur)="markFieldAsTouched('firmName')"
        />
      </div>

    </div>
  </section>

  <!-- SECTION 2b: SEDE OPERATIVA (multi-sede) -->
  <app-operational-sites-section
    [sameAsLegal]="operatingSameAsLegal()"
    (sameAsLegalChange)="operatingSameAsLegal.set($event)"
    [sites]="operatingSites()"
    (sitesChange)="operatingSites.set($event)"
    [readonly]="isReadOnly()"
    [formSubmitted]="formSubmitted()"
  />

  <!-- SECTION 3: ALTRE INFORMAZIONI (Polizza RCT) -->
  <section class="surface-0 border-round p-5 mb-4 shadow-1" role="region" aria-labelledby="section-insurance">
    <div class="flex align-items-center gap-3 mb-4 pb-3 border-bottom-1 border-200">
      <i class="pi pi-shield text-3xl text-primary" aria-hidden="true"></i>
      <div>
        <h2 id="section-insurance" class="text-2xl font-semibold m-0 text-900">Altre Informazioni</h2>
        <p class="text-600 m-0 mt-1">Polizza Responsabilità Civile Professionale</p>
      </div>
    </div>

    <div class="formgrid grid">
      <!-- Professional Liability Policy Number -->
      <div class="field col-12 md:col-4">
        <label for="policy-number" class="font-semibold block mb-2">
          Polizza RCT n°
          <span class="text-red-500" aria-hidden="true">*</span>
          <span class="visually-hidden">obbligatorio</span>
        </label>
        <p-inputnumber
          inputId="policy-number"
          [ngModel]="rctPolicyNumber()"
          (ngModelChange)="rctPolicyNumber.set($event); markFieldAsTouched('rctPolicyNumber')"
          [useGrouping]="false"
          placeholder="Inserisci numero polizza"
          styleClass="w-full"
          (onBlur)="markFieldAsTouched('rctPolicyNumber')"
          [attr.aria-required]="true"
          [attr.aria-invalid]="(formSubmitted() || touchedFields.has('rctPolicyNumber')) && !rctPolicyNumber()"
        />
        @if ((formSubmitted() || touchedFields.has('rctPolicyNumber')) && !rctPolicyNumber()) {
          <small class="text-red-500" role="alert">Campo obbligatorio</small>
        }
      </div>

      <!-- Insurance Company -->
      <div class="field col-12 md:col-4">
        <label for="insurance-company" class="font-semibold block mb-2">
          Compagnia
        </label>
        <input
          id="insurance-company"
          type="text"
          pInputText
          [ngModel]="insuranceCompany()"
          (ngModelChange)="insuranceCompany.set($event); markFieldAsTouched('insuranceCompany')"
          placeholder="Es: Generali"
          class="w-full"
          [disabled]="isReadOnly()"
          (blur)="markFieldAsTouched('insuranceCompany')"
        />
      </div>

      <!-- Coverage Limit -->
      <div class="field col-12 md:col-4">
        <label for="coverage-limit" class="font-semibold block mb-2">
          Massimale
        </label>
        <p-inputnumber
          inputId="coverage-limit"
          [ngModel]="coverageLimit()"
          (ngModelChange)="coverageLimit.set($event); markFieldAsTouched('coverageLimit')"
          [useGrouping]="true"
          placeholder="Es: 5000000"
          styleClass="w-full"
          mode="currency"
          currency="EUR"
          locale="it-IT"
          (onBlur)="markFieldAsTouched('coverageLimit')"
        />
      </div>

      <!-- Agency Of -->
      <div class="field col-12 md:col-4">
        <label for="agency-of" class="font-semibold block mb-2">
          Agenzia di
        </label>
        <input
          id="agency-of"
          type="text"
          pInputText
          [ngModel]="agency()"
          (ngModelChange)="agency.set($event); markFieldAsTouched('agency')"
          placeholder="Es: Milano Centro"
          class="w-full"
          [disabled]="isReadOnly()"
          (blur)="markFieldAsTouched('agency')"
        />
      </div>
    </div>
  </section>

  <!-- I collaboratori sono ora gestiti nella sezione dedicata "Impiegati Amministrativi e Collaboratori" del wizard. -->
  @if (!professionalIdInput()) {

  <!-- SECTION 5: AREE DI COMPETENZA -->
  <section class="surface-0 border-round p-5 mb-4 shadow-1" role="region" aria-labelledby="section-competence-areas">
    <div class="flex align-items-center justify-content-between mb-4 pb-3 border-bottom-1 border-200">
      <div class="flex align-items-center gap-3">
        <i class="pi pi-map text-3xl text-primary" aria-hidden="true"></i>
        <div>
          <h2 id="section-competence-areas" class="text-2xl font-semibold m-0 text-900">Aree di Competenza</h2>
          <p class="text-600 m-0 mt-1">Specifica le aree geografiche di competenza (opzionale)</p>
        </div>
      </div>
      @if (!isReadOnly()) {
        <button
          pButton
          type="button"
          label="Aggiungi Area"
          icon="pi pi-plus"
          class="p-button-sm"
          (click)="addCompetenceArea()"
          [attr.aria-label]="'Aggiungi nuova area di competenza'"
        ></button>
      }
    </div>

    <!-- Empty State -->
    @if (competenceAreas().length === 0) {
      <div class="text-center p-5 text-600">
        <i class="pi pi-map text-5xl mb-3 block text-300" aria-hidden="true"></i>
        <p class="m-0">Nessuna area di competenza aggiunta</p>
        <p class="text-sm mt-2">Clicca su "Aggiungi Area" per iniziare</p>
      </div>
    }

    <!-- Competence Areas List -->
    @for (area of competenceAreas(); track area.id) {
      <div class="surface-50 border-round p-4 mb-3">
        <div class="flex align-items-center justify-content-between mb-3">
          <h3 class="text-lg font-semibold m-0 text-900">
            Area {{ $index + 1 }}
          </h3>
          @if (!isReadOnly()) {
            <button
              pButton
              type="button"
              icon="pi pi-trash"
              class="p-button-rounded p-button-danger p-button-text p-button-sm"
              (click)="removeCompetenceArea(area.id)"
              [attr.aria-label]="'Rimuovi area ' + ($index + 1)"
              pTooltip="Elimina area"
              tooltipPosition="left"
            ></button>
          }
        </div>

        <div class="formgrid grid">
          <!-- Region -->
          <div class="field col-12 md:col-4">
            <label [attr.for]="'area-region-' + area.id" class="font-semibold block mb-2">
              Regione
              <span class="text-red-500" aria-hidden="true">*</span>
              <span class="visually-hidden">obbligatorio</span>
            </label>
            <input
              [attr.id]="'area-region-' + area.id"
              type="text"
              pInputText
              [(ngModel)]="area.region"
              placeholder="Es: Lombardia"
              class="w-full"
              [disabled]="isReadOnly()"
              (blur)="markFieldAsTouched('area-' + area.id + '-region')"
              [attr.aria-required]="true"
              [attr.aria-invalid]="(formSubmitted() || touchedFields.has('area-' + area.id + '-region')) && !area.region"
            />
            @if ((formSubmitted() || touchedFields.has('area-' + area.id + '-region')) && !area.region) {
              <small class="text-red-500" role="alert">Campo obbligatorio</small>
            }
          </div>

          <!-- Court of Appeal -->
          <div class="field col-12 md:col-4">
            <label [attr.for]="'area-court-appeal-' + area.id" class="font-semibold block mb-2">
              Corte di appello (distretto)
            </label>
            <input
              [attr.id]="'area-court-appeal-' + area.id"
              type="text"
              pInputText
              [(ngModel)]="area.courtOfAppeal"
              placeholder="Es: Milano"
              class="w-full"
              [disabled]="isReadOnly()"
            />
          </div>

          <!-- Tribunal -->
          <div class="field col-12 md:col-4">
            <label [attr.for]="'area-tribunal-' + area.id" class="font-semibold block mb-2">
              Tribunale (circondario)
            </label>
            <input
              [attr.id]="'area-tribunal-' + area.id"
              type="text"
              pInputText
              [(ngModel)]="area.tribunal"
              placeholder="Es: Milano"
              class="w-full"
              [disabled]="isReadOnly()"
            />
          </div>

          <!-- Justice of Peace -->
          <div class="field col-12 md:col-4">
            <label [attr.for]="'area-justice-peace-' + area.id" class="font-semibold block mb-2">
              Giudice di Pace (Indicare gli Uffici)
            </label>
            <input
              [attr.id]="'area-justice-peace-' + area.id"
              type="text"
              pInputText
              [(ngModel)]="area.justiceOfPeace"
              placeholder="Es: Milano, Monza"
              class="w-full"
              [disabled]="isReadOnly()"
            />
          </div>
        </div>
      </div>
    }
  </section>
  }

  <!-- Action Buttons (hidden in review mode) -->
  @if (!professionalIdInput()) {
  <div class="flex justify-content-end gap-3 mt-4">
    <button
      pButton
      type="button"
      label="Salva Bozza"
      icon="pi pi-save"
      class="p-button-outlined"
      [disabled]="isSaving()"
      [loading]="isSaving()"
      pTooltip="Salva i dati come bozza senza validazione completa"
      tooltipPosition="top"
      (click)="saveDraft()"
      [attr.aria-label]="'Salva i dati come bozza'"
    ></button>
    <button
      pButton
      type="button"
      label="Salva e continua"
      icon="pi pi-check"
      [disabled]="isSaving()"
      [loading]="isSaving()"
      pTooltip="Salva e valida i dati inseriti"
      tooltipPosition="top"
      (click)="saveAndContinue()"
      [attr.aria-label]="'Salva e valida i dati inseriti'"
    ></button>
  </div>
  }
</div>
